import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../contexts/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const EXCHANGE_RATE_INR_TO_CNY = 0.083;

function ProductShowcase({ category }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 960px
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage") || "en"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [showSearchInput, setShowSearchInput] = useState(false);

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...products];
    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.model.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    filtered = filtered.filter((product) => {
      const finalPrice = product.price - product.discountPrice;
      return finalPrice >= priceRange[0] && finalPrice <= priceRange[1];
    });
    if (showInStockOnly) {
      filtered = filtered.filter((product) => product.inStock);
    }
    switch (sortBy) {
      case "priceLowToHigh":
        filtered.sort(
          (a, b) => a.price - a.discountPrice - (b.price - b.discountPrice)
        );
        break;
      case "priceHighToLow":
        filtered.sort(
          (a, b) => b.price - b.discountPrice - (a.price - a.discountPrice)
        );
        break;
      case "name":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    setFilteredProducts(filtered);
  }, [products, searchTerm, priceRange, showInStockOnly, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/products`);
      const fetchedProducts = response.data || [];
      setProducts(fetchedProducts);
      setFilteredProducts(fetchedProducts);
      const max = Math.max(
        ...fetchedProducts.map((p) => p.price - p.discountPrice)
      );
      setMaxPrice(max > 0 ? Math.ceil(max) : 10000);
      setPriceRange([0, max > 0 ? Math.ceil(max) : 10000]);
      setError(null);
    } catch (err) {
      setError("Failed to fetch products. Please try again later.");
      toast.error("Error loading products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    const handleStorageChange = () => {
      setSelectedLanguage(localStorage.getItem("selectedLanguage") || "en");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [category]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const handlePriceRangeChange = (event, newValue) => setPriceRange(newValue);
  const handleStockFilterChange = (event) =>
    setShowInStockOnly(event.target.checked);
  const handleSortChange = (event) => setSortBy(event.target.value);
  const toggleFilters = () => setShowFilters(!showFilters);
  const toggleSearchInput = () => setShowSearchInput(!showSearchInput);

  const handleCardClick = (productId) => navigate(`/product/${productId}`);

  const handleAddToCart = async (event, product) => {
    event.stopPropagation();
    
    // Check if user is logged in, if not redirect to login
    if (!user) {
      toast.error("Please log in to add items to cart");
      navigate("/login");
      return;
    }
    
    try {
      setLoadingProducts((prev) => ({ ...prev, [product._id]: true }));
      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        toast.error("User not logged in!");
        navigate("/login");
        return;
      }
      const userResponse = await axios.get(
        `${API_BASE_URL}/users/getUserId/${userEmail}`
      );
      const userId = userResponse.data.userId;
      if (!userId) {
        toast.error("User ID not found!");
        return;
      }
      await axios.post(`${API_BASE_URL}/cart/add`, {
        userId,
        productId: product._id,
        quantity: 1,
      });
      toast.success(`${product.name} added to cart!`, {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
    } catch (error) {
      toast.error("Failed to add item to cart");
    } finally {
      setLoadingProducts((prev) => ({ ...prev, [product._id]: false }));
    }
  };

  const getGridSize = () => {
    if (isMobile) return 6; // 2 columns below 600px
    if (isTablet) return 4; // 3 columns 600px-960px
    return 3; // 4 columns above 960px
  };

  const getPriceDetails = (price, discountPrice) => {
    const isChinese = selectedLanguage === "zh-TW";
    const currencySymbol = isChinese ? "¥" : "₹";
    const conversionRate = isChinese ? EXCHANGE_RATE_INR_TO_CNY : 1;
    return {
      symbol: currencySymbol,
      finalPrice: ((price - discountPrice) * conversionRate).toFixed(2),
      originalPrice: (price * conversionRate).toFixed(2),
      discount: (discountPrice * conversionRate).toFixed(2),
    };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      id="productshowcase"
      sx={{
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2, md: 4 },
        maxWidth: "100%",
        width: {
          xs: "100%",
          sm: "90%",
          md: "1200px",
          lg: "1400px",
          xl: "1600px",
        },
        margin: "0 auto",
        bgcolor: "background.default",
        borderRadius: 2,
        boxShadow: 3,
        position: "relative", // Ensure proper stacking context
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          sx={{
            color: "primary.main",
            fontWeight: "bold",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
            textAlign: isMobile ? "center" : "start",
            width: "100%",
            mb: { xs: 2, sm: 0 },
          }}
        >
          {category} Products
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1, sm: 2 },
            justifyContent: { xs: "center", sm: "flex-end" },
            width: { xs: "100%", sm: "auto" },
            alignItems: "center",
            position: "relative", // For positioning the search input
          }}
        >
          <IconButton
            onClick={toggleSearchInput}
            sx={{
              color: "primary.main",
              p: { xs: 0.5, sm: 1 },
              // mb: { xs: 1, sm: 2 }, // margin bottom added (adjust values as needed)
              "&:hover": { bgcolor: "rgba(107, 162, 187, 0.2)" },
            }}
          >
            <SearchIcon />
          </IconButton>
          {showSearchInput && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "absolute",
                top: "100%", // Position below the icon
                right: { xs: "auto", sm: "50px" }, // Adjust for sort and filter icons on larger screens
                left: { xs: "0", sm: "auto" }, // Full width on mobile
                zIndex: 10, // Hover above other content
                marginTop: "10px",
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Search by name or model..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "primary.main", mr: 1 }} />
                  ),
                }}
                sx={{
                  width: {
                    xs: "calc(100vw - 32px)", // Full width minus padding on mobile
                    sm: "250px", // Fixed width on tablet
                    md: "300px", // Slightly larger on desktop
                    lg: "350px", // Larger on large screens
                    xl: "400px", // Max width on extra large screens
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "rgb(223, 223, 223)",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    height: { xs: "40px", sm: "48px" },
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    color: "#111111", // Very dark input text
                    "& input::placeholder": {
                      color: "#111111", // Very dark placeholder text
                      opacity: 1, // Make sure placeholder is fully opaque
                    },
                    "& fieldset": { borderColor: "primary.light" },
                    "&:hover fieldset": { borderColor: "primary.main" },
                    "&.Mui-focused fieldset": { borderColor: "primary.dark" },
                  },
                }}
              />
            </motion.div>
          )}
          <IconButton
            onClick={toggleFilters}
            sx={{
              color: "primary.main",
              p: { xs: 0.5, sm: 1 },
              "&:hover": { bgcolor: "rgba(107, 162, 187, 0.2)" },
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            size="small"
            startAdornment={<SortIcon sx={{ mr: 1 }} />}
            sx={{
              bgcolor: "rgba(107, 162, 187, 0.8)",
              color: "black",
              width: { xs: "120px", sm: "150px" },
              fontSize: { xs: "0.8rem", sm: "1rem" },
              borderRadius: 1,
            }}
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="priceLowToHigh">Price: Low</MenuItem>
            <MenuItem value="priceHighToLow">Price: High</MenuItem>
            <MenuItem value="name">Name (A-Z)</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Filter Section */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ marginTop: "70px" }}
        >
          <Card
            sx={{
              mt: 2,
              p: { xs: 2, sm: 3 },
              width: { xs: "100%", sm: "95%", md: "450px" },
              maxWidth: "450px",
              mx: "auto",
              bgcolor: "rgba(255, 255, 255, 0.95)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.15)",
              borderRadius: 4,
              border: "1px solid rgba(107, 162, 187, 0.2)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                boxShadow: "0 12px 48px rgba(31, 38, 135, 0.2)",
                transform: "translateY(-4px)",
              },
            }}
          >
            {/* Header */}
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: "#1a237e",
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.5rem" },
                letterSpacing: 0.5,
              }}
            >
              Filter Options
            </Typography>

            {/* Price Range Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: "#424242",
                  fontWeight: 500,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  mb: 1.5,
                }}
              >
                Price Range ({getPriceDetails(0, 0).symbol})
              </Typography>
              <Slider
                value={priceRange}
                onChange={handlePriceRangeChange}
                valueLabelDisplay="auto"
                min={0}
                max={maxPrice}
                sx={{
                  color: "#3f51b5",
                  "& .MuiSlider-thumb": {
                    width: 16,
                    height: 16,
                    bgcolor: "#fff",
                    border: "2px solid #3f51b5",
                    "&:hover": {
                      boxShadow: "0 0 0 8px rgba(63, 81, 181, 0.16)",
                    },
                  },
                  "& .MuiSlider-rail": {
                    opacity: 0.3,
                    bgcolor: "#bfbfbf",
                  },
                  "& .MuiSlider-track": {
                    border: "none",
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                  color: "#666",
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                }}
              >
                <span>{getPriceDetails(0, 0).symbol}0</span>
                <span>
                  {getPriceDetails(0, 0).symbol}
                  {maxPrice}
                </span>
              </Box>
            </Box>

            {/* Divider */}
            <Divider
              sx={{
                my: 2,
                bgcolor: "rgba(107, 162, 187, 0.2)",
              }}
            />

            {/* Stock Filter */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInStockOnly}
                  onChange={handleStockFilterChange}
                  sx={{
                    color: "#3f51b5",
                    "&.Mui-checked": {
                      color: "#3f51b5",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: { xs: 20, sm: 24 },
                    },
                  }}
                />
              }
              label="Show In Stock Only"
              sx={{
                color: "#424242",
                "& .MuiTypography-root": {
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  fontWeight: 500,
                },
                "&:hover": {
                  color: "#3f51b5",
                },
              }}
            />
          </Card>
        </motion.div>
      )}

      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ display: "flex", justifyContent: "center", px: { xs: 1, sm: 0 } }}
        className="mt-5"
      >
        {filteredProducts.length === 0 ? (
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              width: "100%",
              mt: 4,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            No products found matching your search
          </Typography>
        ) : (
          filteredProducts.map((product) => {
            const { symbol, finalPrice, originalPrice, discount } =
              getPriceDetails(product.price, product.discountPrice);

            return (
              <Grid item xs={6} sm={4} md={getGridSize()} key={product._id}>
                <Card
                  onClick={() => handleCardClick(product._id)}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 4,
                    cursor: "pointer",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: "100%",
                      height: { xs: 120, sm: 150, md: 180 },
                      objectFit: "contain",
                      backgroundColor: "white",
                      transition: "transform 0.3s ease",
                      "&:hover": { transform: "scale(1.03)" },
                    }}
                    image={`https://covercraft-x28n.onrender.com${product.image}`}
                    alt={product.name}
                  />
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      p: { xs: 1, sm: 2 },
                      boxShadow: "inset 0px 0px 10px rgba(255, 255, 255, 0.8)",
                      borderRadius: "0px 0px 0px 12px",
                      bgcolor: "rgba(107, 162, 187, 0.8)",
                    }}
                  >
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      sx={{
                        color: "text.primary",
                        fontWeight: "bold",
                        mb: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" },
                        "@media (max-width: 768px)": {
                          height: "6vh",
                          fontSize: "0.8rem",
                          lineHeight: "1.2",
                          display: "-webkit-box",
                          "-webkit-box-orient": "vertical",
                          "-webkit-line-clamp": "2",
                          overflow: "hidden",
                        },
                      }}
                    >
                      <span
                        style={{
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          fontWeight: "500",
                          color: "#888",
                          background:
                            "linear-gradient(45deg,rgb(255, 255, 255),rgb(255, 255, 255))",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          padding: "0.2em 0.4em",
                          borderRadius: "4px",
                        }}
                      >
                        {product.model}
                      </span>
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: { sm: "space-between" },
                        alignItems: "center",
                        mt: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="rgb(38, 0, 255)"
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" },
                        }}
                      >
                        {symbol}
                        {finalPrice}
                      </Typography>
                      {product.discountPrice > 0 && (
                        <>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              fontWeight: "bold",
                              textDecoration: "line-through",
                              mx: { xs: 0, sm: 1 },
                              mt: { xs: 0.5, sm: 0 },
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                            }}
                          >
                            {symbol}
                            {originalPrice}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0,
                              color: "text.secondary",
                              fontSize: {
                                xs: "0.6rem",
                                sm: "0.7rem",
                                md: "0.8rem",
                              },
                            }}
                          >
                            Save {symbol}
                            {discount}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={(e) => handleAddToCart(e, product)}
                      disabled={
                        !product.inStock || loadingProducts[product._id]
                      }
                      sx={{
                        mt: 2,
                        bgcolor: "rgba(28, 226, 134, 0.7)",
                        color: "black",
                        fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1rem" },
                        py: { xs: 0.5, sm: 1, md: 1.5 },
                        width: { xs: "100%", sm: "auto" },
                        boxShadow: "0 0 10px rgba(0, 255, 255, 0.5)",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        transition: "0.3s ease-in-out",
                        "&:hover": {
                          bgcolor: "rgba(250, 95, 191, 0.7)",
                          color: "white",
                          boxShadow:
                            "0 0 20px rgba(236, 43, 117, 0.8), 0 0 40px rgba(255, 251, 0, 0.6)",
                        },
                      }}
                    >
                      <Box>
                        {loadingProducts[product._id] ? (
                          <>
                            <CircularProgress
                              size={24}
                              sx={{
                                position: "absolute",
                                left: "50%",
                                marginLeft: "-12px",
                                color: "white",
                              }}
                            />
                            <span style={{ opacity: 0 }}>Add to Cart</span>
                          </>
                        ) : (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: { xs: "9px", sm: "12px", md: "14px" },
                            }}
                          >
                            Add to Cart
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
}

export default ProductShowcase;
